import type { PricingType, RevenueSplitMode, RoomModelStatus, RoomModelSource } from '@modelcast/types';

export type ModelOption = 'none' | 'self' | 'invite' | 'ad';

export interface SelectedModel {
  id?: string;
  profileId: string | null; // null when inviting by email only
  email: string | null; // For email-only invites
  displayName: string;
  photoUrl: string | null;
  source?: RoomModelSource;
  status?: RoomModelStatus;
  splitPercent: number;
  guarantee: number | null;
  inviteId?: string | null;
  emailSentAt?: string | null;
  rsvpStatus?: string | null;
}

export interface CreateFormState {
  // Step 1: Event Details
  name: string;
  description: string;
  scheduledAt: string;
  duration: string; // Duration in format like "1h 30m" or "90m"
  timezone: string;
  isPublic: boolean; // Whether listed on public homepage
  isTutored: boolean; // Whether it's a tutored session
  maxAttendees: number | null; // Optional max number of attendees
  eventImage: File | null; // Event image selected for upload
  existingImageUrl: string | null; // Existing image URL when editing
  imageArtistCredit: string;
  imageModelCredit: string;

  // Step 2: Model & Pricing
  modelOption: ModelOption; // Which model option is selected
  selectedModel: SelectedModel | null; // First selected model kept for older navigation payloads
  selectedModels: SelectedModel[];
  modelExpectations: string; // Message sent to models
  modelAdText: string; // Public job ad text
  pricingType: PricingType;
  currency: string; // ISO 4217 code from SUPPORTED_CURRENCIES (USD, GBP, EUR, …)
  ticketPrice: number | null; // Price in major units (converted to cents when saving)
  suggestedMinimum: number | null; // Suggested donation in major units
  modelSplitPercent: number; // 0-100, model gets this %, organizer gets the rest
  modelGuarantee: number | null; // Guarantee amount in dollars
  revenueSplitMode: RevenueSplitMode;
}

export const initialFormState: CreateFormState = {
  // Step 1
  name: '',
  description: '',
  scheduledAt: '',
  duration: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  isPublic: false,
  isTutored: false,
  maxAttendees: null,
  eventImage: null,
  existingImageUrl: null,
  imageArtistCredit: '',
  imageModelCredit: '',
  // Step 2
  modelOption: 'none',
  selectedModel: null,
  selectedModels: [],
  modelExpectations: '',
  modelAdText: '',
  pricingType: 'donation',
  currency: 'USD',
  ticketPrice: null,
  suggestedMinimum: null,
  modelSplitPercent: 75, // First external model defaults to 75% untutored, even split tutored
  modelGuarantee: null,
  revenueSplitMode: 'manual',
};
